import * as React from "react";
import { Icon, IconName } from "./Icon";

type Variant =
  | "solid"
  | "navcta"
  | "outline"
  | "buy"
  | "readmore"
  | "ghost"
  | "link"
  | "icon";
// "dark"/"light" name the background the control sits on. "floating" is for a
// control that sits over an UNKNOWN/any background (e.g. the global sound
// toggle, which floats over both the light reading cover and the dark-blue 3D
// scene) — it carries its own contrasting chip so it reads on anything.
// Currently honoured only by the `icon` variant; other variants treat it as
// "dark".
type Surface = "dark" | "light" | "floating";

type TextProps = {
  variant: Exclude<Variant, "icon">;
  surface?: Surface;
  tone?: "solid" | "muted";
  /** Decorative icon before the label. Use instead of inline glyphs/arrows. */
  leftIcon?: IconName;
  /** Decorative icon after the label. Use instead of inline glyphs/arrows. */
  rightIcon?: IconName;
  /** Stretch to the container width. Call-site driven — used for stacked
   *  full-width CTAs on mobile forms (the form decides, not the viewport). */
  fluid?: boolean;
  /** Control size. "sm" tightens the padding one further step below the
   *  default at every breakpoint — for a row that must stay on one line even
   *  where the default would wrap. Do NOT pass it just because the viewport is
   *  a phone: every variant already compacts itself there. Honoured by `solid`
   *  and `outline` on-light; other variants have fixed lockups and ignore it. */
  size?: "md" | "sm";
  /** Busy state: disables the button and shows a spinning icon in place of
   *  leftIcon (e.g. while a download/submit is in flight). */
  loading?: boolean;
  className?: string;
  children: React.ReactNode;
};

type IconOnlyProps = {
  variant: "icon";
  surface?: Surface;
  /** Required: the glyph rendered inside the square tap target. */
  icon: IconName;
  /** Required: accessible label since there is no visible text. */
  ariaLabel: string;
  className?: string;
};

type CommonProps = TextProps | IconOnlyProps;

type ButtonAsButton = CommonProps & {
  as?: "button";
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps | "aria-label">;

type ButtonAsAnchor = CommonProps & {
  as: "a";
  href: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps | "aria-label">;

type Props = ButtonAsButton | ButtonAsAnchor;

const SHARED_CLASS =
  "inline-flex items-center justify-center uppercase transition focus:outline-none";

// Every text variant carries a compacted mobile padding with a `md:` override
// back to the desktop lockup. Phones give a control a third of the width a
// laptop does, so the desktop padding turns a pair of CTAs into a wall — the
// component owns that adaptation (mirroring Price's mobile digit scale) rather
// than leaving each call site to pass a smaller `size`. Vertical padding stays
// generous enough to keep every control at a ~44px tap target.
function variantClasses(
  variant: Variant,
  tone: "solid" | "muted",
  surface: Surface,
  size: "md" | "sm" = "md",
): string {
  const pad =
    size === "sm" ? "px-3.5 py-2.5 md:px-5 md:py-3.5" : "px-4 py-3 md:px-10 md:py-4";
  if (variant === "solid") {
    // The canonical filled brand-blue CTA — used for every primary action
    // (book, buy, submit). Flat fill, no left flag.
    const bg = tone === "muted" ? "bg-primary/80 hover:bg-primary" : "bg-primary hover:opacity-90";
    const ring =
      surface === "light"
        ? "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
        : "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black";
    return [
      "relative",
      bg,
      "text-white t-button",
      pad,
      ring,
    ].join(" ");
  }

  if (variant === "navcta") {
    // Rounded, floating CTA inside the navbar pill — design-system2 navbar.
    // Diagonal gradient (lighter top-left → darker bottom-right), 14px radius,
    // a soft lift shadow and a looping sheen sweep (`.btn-nav-sheen`,
    // reduced-motion gated in SCSS). Stays one line so a long localized label
    // never wraps.
    return [
      "group btn-nav-sheen",
      "text-white hover:opacity-95",
      "bg-[linear-gradient(135deg,#4d76a4_0%,#2a4d71_100%)]",
      "px-4 py-2 md:px-[26px] md:py-[12px] rounded-[14px]",
      "text-[11px] font-semibold tracking-[0.13em] whitespace-nowrap",
      "shadow-[0_6px_16px_rgba(26,53,80,0.18)]",
      "focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    ].join(" ");
  }

  if (variant === "readmore") {
    // Solid primary, white left-stripe, arrow growth on hover.
    // Source: src/design-system/screens/Mediumship Decks.html — .btn-readmore
    return [
      "relative group",
      "bg-primary text-white t-button",
      "py-3 pl-8 pr-6 md:py-4 md:pl-12 md:pr-10",
      "shadow-[0_12px_32px_rgba(26,53,80,0.18)]",
      "hover:bg-primary-hover hover:-translate-y-px hover:shadow-[0_16px_40px_rgba(26,53,80,0.22)]",
      "active:bg-primary-press active:translate-y-0",
      "transition-[background,transform,box-shadow] duration-200",
      "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    ].join(" ");
  }

  if (variant === "icon") {
    // 44px square at every breakpoint: an icon-only control has no label to
    // fall back on, so it keeps the full tap target where fingers replace a
    // cursor. This is the one variant that does NOT compact on mobile.
    if (surface === "floating") {
      // Self-contained chip for a control floating over an unknown background.
      // Brand-blue fill + white icon read on the light reading cover; the
      // white hairline border + drop shadow keep the chip defined against the
      // dark-blue 3D scene where the fill would otherwise blend in.
      return [
        "w-11 h-11 text-[20px] rounded-full",
        "bg-primary text-white hover:bg-primary-hover",
        "border border-white/25 shadow-[0_4px_16px_rgba(0,0,0,0.28)]",
        "transition-colors duration-200",
        "focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
      ].join(" ");
    }
    if (surface === "light") {
      return [
        "w-11 h-11 text-[20px]",
        "text-primary/70 hover:text-primary",
        "transition-colors duration-200",
        "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
      ].join(" ");
    }
    return [
      "w-11 h-11 text-[20px]",
      "text-white/80 hover:text-white",
      "transition-colors duration-200",
      "focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
    ].join(" ");
  }

  if (variant === "ghost") {
    // Kit .btn-ghost — secondary/cancel. Transparent, muted primary label,
    // sharp corners, hover deepens to full primary.
    return [
      "bg-transparent text-primary/60",
      "px-3.5 py-2.5 md:px-5 md:py-3.5",
      "text-eyebrow font-medium uppercase tracking-link",
      "hover:text-primary",
      "transition-colors duration-200",
      "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    ].join(" ");
  }

  if (variant === "link") {
    // Text-only link, no chrome — mirrors the BackLink component
    // (t-eyebrow, color-only hover). Arrow/icon comes via children.
    if (surface === "light") {
      return [
        "gap-2.5 t-eyebrow",
        "text-primary/60 hover:text-primary",
        "transition-colors duration-200",
        "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
      ].join(" ");
    }
    return [
      "gap-2.5 t-eyebrow",
      "text-white/70 hover:text-white",
      "transition-colors duration-200",
      "focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
    ].join(" ");
  }

  if (variant === "outline") {
    if (surface === "light") {
      // Outline-on-light: primary-blue border + label, hover inverts to filled
      // primary with white text. Padding + type scale match `primary` so the
      // button can sit side-by-side with BUY NOW at equal height.
      return [
        "border-2 border-primary text-primary t-button",
        pad,
        "hover:bg-primary hover:text-white",
        "transition-colors duration-200",
        "focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
      ].join(" ");
    }
    // Footer LEAVE A TIP (surface="dark"): t-label-heavy scale (11px / 0.20em / 600)
    // intentionally tighter than .t-button (12px / 0.16em) per the kit's
    // outline-on-primary lockup.
    return [
      "border-2 border-white text-white",
      "px-4 py-2 md:px-[26px] md:py-2.5",
      "t-label-heavy",
      "hover:bg-white hover:text-primary",
      "focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
    ].join(" ");
  }

  // variant === "buy"
  // Decks BUY NOW on blue: t-label-heavy scale (11px / 0.20em / 600) on a
  // 1px white border with bg-white/10. Same scale as the outline footer CTA.
  return [
    "border border-white bg-white/10 text-white",
    "px-5 py-2.5 md:px-9 md:py-3.5",
    "t-label-heavy",
    "hover:bg-white hover:text-primary",
    "focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary",
  ].join(" ");
}

export function Button(props: Props) {
  const variant = props.variant;
  const surface: Surface = props.surface ?? "dark";
  const className = (props as { className?: string }).className;

  // Discriminate once for the rest of the function.
  if (variant === "icon") {
    const { icon, ariaLabel } = props as IconOnlyProps;
    const cls = [
      SHARED_CLASS,
      variantClasses(variant, "solid", surface),
      className,
    ]
      .filter(Boolean)
      .join(" ");
    const rest = props as IconOnlyProps & {
      as?: "button" | "a";
      href?: string;
    } & Record<string, unknown>;
    const {
      variant: _v,
      surface: _s,
      icon: _i,
      ariaLabel: _a,
      className: _c,
      as,
      href,
      ...restAttrs
    } = rest;
    if (as === "a") {
      const anchorAttrs = restAttrs as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <a href={href} className={cls} aria-label={ariaLabel} {...anchorAttrs}>
          <Icon name={icon} />
        </a>
      );
    }
    const buttonAttrs = restAttrs as React.ButtonHTMLAttributes<HTMLButtonElement>;
    return (
      <button type="button" className={cls} aria-label={ariaLabel} {...buttonAttrs}>
        <Icon name={icon} />
      </button>
    );
  }

  // Text-bearing variants — same code path as before.
  const text = props as TextProps;
  const tone: "solid" | "muted" = text.tone ?? "solid";
  const { leftIcon, rightIcon, fluid, loading, children, size } = text;
  const rest = text as TextProps & {
    as?: "button" | "a";
    href?: string;
  } & Record<string, unknown>;
  const isDisabled = Boolean((rest as { disabled?: boolean }).disabled) || Boolean(loading);
  const disabledCls = isDisabled
    ? "opacity-35 cursor-not-allowed pointer-events-none"
    : "";
  const iconGap = leftIcon || rightIcon || loading ? "gap-2.5" : "";
  const fluidCls = fluid ? "w-full" : "";
  const cls = [
    SHARED_CLASS,
    variantClasses(variant, tone, surface, size),
    iconGap,
    fluidCls,
    disabledCls,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const stripe =
    variant === "readmore" ? (
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[5px] bg-white" />
    ) : null;

  const arrow =
    variant === "readmore" ? (
      <span
        aria-hidden="true"
        className="relative ml-3 inline-block h-px w-[18px] bg-current before:content-[''] before:absolute before:right-[-1px] before:top-1/2 before:h-2 before:w-2 before:-translate-y-1/2 before:rotate-45 before:border-t before:border-r before:border-current"
      />
    ) : null;

  const content = (
    <>
      {stripe}
      {loading ? (
        <span aria-hidden="true" className="inline-flex animate-spin">
          <Icon name="refresh" />
        </span>
      ) : (
        leftIcon && <Icon name={leftIcon} />
      )}
      {children}
      {rightIcon && <Icon name={rightIcon} />}
      {arrow}
    </>
  );

  const {
    variant: _v,
    surface: _s,
    tone: _t,
    leftIcon: _li,
    rightIcon: _ri,
    fluid: _f,
    size: _sz,
    loading: _ld,
    children: _ch,
    className: _cn,
    as,
    href,
    ...restAttrs
  } = rest;

  if (as === "a") {
    const anchorAttrs = restAttrs as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a href={href} className={cls} aria-busy={loading || undefined} {...anchorAttrs}>
        {content}
      </a>
    );
  }
  const buttonAttrs = restAttrs as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type="button" className={cls} aria-busy={loading || undefined} {...buttonAttrs}>
      {content}
    </button>
  );
}
